function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { EMPTY, from, switchMap, tap } from 'rxjs';
import { useObservable } from 'rxjs-hooks';
import { Box, Divider, Link, Typography } from '@mui/material';
import { AlertMessage, LinkButton } from '@procom-labs/atoms';
import { EmailSupportLinks, LoginTypes, mfaCodeVerificationLens, MfaTypes, RecaptchaActions, useAuthService, useMfaService, useMfaStore, useRecaptcha, useSubjectSelector, useSubscriptionRef } from '@procom-labs/common';
import { OtpInput, useAlert } from '@procom-labs/molecules';
export var MfaLoginCodeVerification = function(param) {
    var currentCognitoUserEmail = param.currentCognitoUserEmail, _param_showEmail = param.showEmail, showEmail = _param_showEmail === void 0 ? true : _param_showEmail, loginType = param.loginType;
    var authService = useAuthService();
    var mfaService = useMfaService();
    var mfaStore = useMfaStore();
    var contactUsLink = useMemo(function() {
        return (loginType === null || loginType === void 0 ? void 0 : loginType.toLowerCase()) === LoginTypes.Client.toLowerCase() ? "mailto:".concat(EmailSupportLinks.GorillaWorks) : "mailto:".concat(EmailSupportLinks.ClientConnections);
    }, [
        loginType
    ]);
    var t = useTranslation('main').t;
    var addAlert = useAlert().addAlert;
    var getRecaptchaToken = useRecaptcha().getRecaptchaToken;
    var currentCognitoUser = useObservable(function() {
        return authService.currentCognitoUser$;
    });
    var _useSubjectSelector = useSubjectSelector(mfaStore, [
        'preferredMfaMethod',
        'mfaType'
    ]), preferredMfaMethod = _useSubjectSelector.preferredMfaMethod, mfaType = _useSubjectSelector.mfaType;
    var subscrptionRef = useSubscriptionRef();
    var cognitoUserHasMultipleMfaMethods = useObservable(function() {
        return authService.cognitoUserHasMultipleMfaMethods$;
    });
    var handleVerifyCode = useCallback(function(otpCode) {
        return from(getRecaptchaToken(RecaptchaActions.Mfa)).pipe(switchMap(function() {
            if (currentCognitoUser && currentCognitoUserEmail) return mfaService.handleSendMfaCode({
                code: otpCode,
                currentCognitoUserEmail: currentCognitoUserEmail,
                currentCognitoUser: currentCognitoUser
            });
            return EMPTY;
        }));
    }, [
        currentCognitoUser,
        currentCognitoUserEmail,
        getRecaptchaToken,
        mfaService
    ]);
    var handleSelectMfaMethod = useCallback(function(type) {
        if (currentCognitoUserEmail) {
            return authService.authenticate({
                username: currentCognitoUserEmail,
                password: authService.currentCognitoUserPasswordSubject.value
            }).pipe(switchMap(function() {
                if (type) return mfaService.handleSelectMfaType({
                    mfaType: type,
                    currentCognitoUser: authService.currentCognitoUserSubject.value || currentCognitoUser,
                    currentCognitoUserEmail: currentCognitoUserEmail
                });
                return EMPTY;
            }));
        }
        return EMPTY;
    }, [
        authService,
        currentCognitoUser,
        currentCognitoUserEmail,
        mfaService
    ]);
    var handleSelectMfaMethodSubscription = useCallback(function(type) {
        var showAlert = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
        subscrptionRef.current = handleSelectMfaMethod(type).subscribe({
            next: function() {
                if (showAlert) addAlert({
                    severity: 'success',
                    message: t('common.mfa.alerts.mfaMethodChanged', {
                        mfaMethod: t("common.mfa.types.".concat(type.toLowerCase()))
                    })
                });
            }
        });
    }, [
        addAlert,
        handleSelectMfaMethod,
        t,
        subscrptionRef
    ]);
    useEffect(function() {
        var subscription = null;
        if (currentCognitoUserEmail && currentCognitoUser && !preferredMfaMethod && cognitoUserHasMultipleMfaMethods) {
            subscription = mfaService.getPreferredMfaMethod(currentCognitoUserEmail).pipe(tap(function(type) {
                mfaStore.dispatch({
                    mfaType: type
                });
                handleSelectMfaMethodSubscription(type);
            })).subscribe();
        }
        return function() {
            if (subscription && !subscription.closed) {
                subscription.unsubscribe();
                subscription = null;
            }
        };
    }, [
        currentCognitoUser,
        cognitoUserHasMultipleMfaMethods,
        currentCognitoUserEmail,
        handleSelectMfaMethodSubscription,
        mfaService,
        mfaStore,
        preferredMfaMethod
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h5",
                component: "h1",
                sx: {
                    textAlign: 'center',
                    marginBlockEnd: 2
                },
                children: t('organisms.mfaLoginCodeVerification.heading')
            }),
            showEmail ? /*#__PURE__*/ _jsx(Typography, {
                variant: "body2",
                sx: {
                    textAlign: 'center',
                    marginBlockEnd: 2
                },
                children: /*#__PURE__*/ _jsx(Trans, {
                    i18nKey: "organisms.mfaLoginCodeVerification.subHeading",
                    values: {
                        email: currentCognitoUserEmail
                    }
                })
            }) : null,
            mfaType && /*#__PURE__*/ _jsx(AlertMessage, {
                severity: "info",
                sx: {
                    marginBlockEnd: 4
                },
                children: t("organisms.mfaLoginCodeVerification.info.".concat(mfaType.toLowerCase()))
            }),
            /*#__PURE__*/ _jsx(Divider, {
                sx: {
                    marginBlockEnd: 4,
                    width: '100%'
                }
            }),
            /*#__PURE__*/ _jsx(OtpInput, _object_spread({
                name: mfaCodeVerificationLens.verificationCode.$key(),
                verifyCode: handleVerifyCode,
                sx: {
                    '& .code-input-ctn': {
                        maxWidth: 250,
                        margin: 'auto'
                    },
                    '& .MuiFormHelperText-root': {
                        textAlign: 'center'
                    },
                    marginBlockEnd: 4
                }
            }, mfaType === MfaTypes.SMS ? {
                sendCode: function() {
                    return handleSelectMfaMethod(MfaTypes.SMS);
                }
            } : {})),
            cognitoUserHasMultipleMfaMethods && preferredMfaMethod && /*#__PURE__*/ _jsx(Box, {
                sx: {
                    textAlign: 'center',
                    marginBlockEnd: 6
                },
                children: preferredMfaMethod === MfaTypes.Authenticator ? /*#__PURE__*/ _jsx(LinkButton, {
                    onClick: function() {
                        return handleSelectMfaMethodSubscription(MfaTypes.SMS, true);
                    },
                    children: t('organisms.mfaLoginCodeVerification.mfaMethodUnavailable.totp')
                }) : /*#__PURE__*/ _jsx(LinkButton, {
                    onClick: function() {
                        return handleSelectMfaMethodSubscription(MfaTypes.Authenticator, true);
                    },
                    children: t('organisms.mfaLoginCodeVerification.mfaMethodUnavailable.sms')
                })
            }),
            /*#__PURE__*/ _jsx(Box, {
                sx: {
                    textAlign: 'center',
                    marginBlockEnd: 6
                },
                children: /*#__PURE__*/ _jsxs(Typography, {
                    variant: "body2",
                    sx: {
                        marginBlockEnd: 2
                    },
                    children: [
                        t('organisms.mfaLoginCodeVerification.havingTrouble'),
                        /*#__PURE__*/ _jsx(Link, {
                            href: contactUsLink,
                            children: t('organisms.mfaLoginCodeVerification.contactUs')
                        })
                    ]
                })
            })
        ]
    });
};
